/**
 * FeedGallery — pagină cronologică de noutăți.
 *
 * Fiecare poză apare într-un card mare (full-width, max-w-2xl), unul sub
 * altul. Poză completă vizibilă (object-contain, max-h-80vh), header cu
 * avatar + nume + timp, caption, like bar, buton expandare comentarii.
 *
 * Click pe card NU deschide nimic — interacțiunile sunt pe butoanele
 * din interior (like, expand comments).
 *
 * Fundal: plin opac (bg-bg-alt), fără nicio transparență.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import type { Photo } from '@/types';
import LikeButton from './LikeButton';
import CommentList from './CommentList';
import { photoThumbnail } from '@/lib/api';
import { recordView } from '@/hooks/useGallery';

interface FeedGalleryProps {
  photos: Photo[];
  mockImageFn?: (photo: Photo) => string;
}

const PAGE_SIZE = 50;

function formatTimeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'chiar acum';
  if (min < 60) return `acum ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `acum ${h} ${h === 1 ? 'oră' : 'ore'}`;
  const d = Math.floor(h / 24);
  if (d < 7) return `acum ${d} ${d === 1 ? 'zi' : 'zile'}`;
  return new Date(ts).toLocaleDateString('ro-RO', {
    day: 'numeric',
    month: 'short',
  });
}

function PhotoComments({ photoId, expanded }: { photoId: string; expanded: boolean }) {
  if (!expanded) return null;
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="border-t border-border bg-bg px-sm pb-sm"
    >
      <CommentList photoId={photoId} />
    </motion.div>
  );
}

export default function FeedGallery({ photos, mockImageFn }: FeedGalleryProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const visiblePhotos = photos.slice(0, visibleCount);
  const hasMore = visibleCount < photos.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '600px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="container-page mx-auto max-w-2xl py-md">
      <div className="mb-md text-center">
        <p className="font-mono text-xs text-fg-soft">
          Cele mai noi poze de la invitați · derulează în jos
        </p>
      </div>

      <div className="space-y-xl">
        {visiblePhotos.map((photo) => {
          const isExpanded = expanded.has(photo.id);

          return (
            <motion.article
              key={photo.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              onViewportEnter={() => recordView(photo.id)}
              transition={{ duration: 0.4 }}
              className="overflow-hidden rounded-md border border-border bg-bg-alt"
              style={{ boxShadow: 'var(--shadow-sm)' }}
            >
              {/* Header: avatar + nume + timp + views */}
              <header className="flex items-center justify-between p-sm bg-bg-alt">
                <div className="flex items-center gap-sm">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-bg-alt font-serif text-sm">
                    {photo.uploader_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium leading-none">
                      {photo.uploader_name}
                    </p>
                    <p className="font-mono text-[10px] text-fg-soft">
                      {formatTimeAgo(photo.uploaded_at)}
                    </p>
                  </div>
                </div>
                <span className="font-mono text-xs text-fg-soft">
                  👁 {photo.views_count ?? 0}
                </span>
              </header>

              {/* Poză completă */}
              <div className="w-full overflow-hidden bg-bg p-2">
                <img
                  src={mockImageFn ? mockImageFn(photo) : photoThumbnail(photo)}
                  alt={photo.caption || `Photo by ${photo.uploader_name}`}
                  loading="lazy"
                  className="w-full h-auto max-h-[80vh] object-contain rounded-sm"
                />
              </div>

              {/* Caption */}
              {photo.caption && (
                <div className="px-sm pt-sm bg-bg-alt">
                  <p className="text-sm">
                    <span className="font-medium">{photo.uploader_name}</span>{' '}
                    <span className="text-fg">{photo.caption}</span>
                  </p>
                </div>
              )}

              {/* Like bar + comment toggle */}
              <div className="flex items-center justify-between px-sm py-sm bg-bg-alt">
                <div className="flex items-center gap-md">
                  <LikeButton photoId={photo.id} initialCount={photo.likes_count} compact />
                  <button
                    onClick={() => toggleExpand(photo.id)}
                    className="flex items-center gap-xs text-sm text-fg-soft transition-colors hover:text-fg"
                  >
                    <span>💬</span>
                    <span>{photo.comments_count}</span>
                  </button>
                </div>
              </div>

              {/* Număr like-uri (ca în versiunea originală) */}
              {photo.likes_count > 0 && (
                <div className="px-sm pb-sm bg-bg-alt">
                  <p className="text-xs font-medium">
                    {photo.likes_count}{' '}
                    {photo.likes_count === 1 ? 'apreciere' : 'aprecieri'}
                  </p>
                </div>
              )}

              {/* Comentarii expandate (inline) */}
              <PhotoComments photoId={photo.id} expanded={isExpanded} />
              {isExpanded && (
                <button
                  onClick={() => toggleExpand(photo.id)}
                  className="block w-full border-t border-border px-sm py-xs text-left text-xs text-fg-soft hover:text-fg"
                >
                  Ascunde comentariile ↑
                </button>
              )}
              {!isExpanded && photo.comments_count > 0 && (
                <button
                  onClick={() => toggleExpand(photo.id)}
                  className="block w-full border-t border-border px-sm py-xs text-left text-xs text-fg-soft hover:text-fg"
                >
                  {photo.comments_count === 1
                    ? 'Vezi comentariul ↓'
                    : `Vezi toate cele ${photo.comments_count} comentarii ↓`}
                </button>
              )}
            </motion.article>
          );
        })}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="py-xl flex flex-col items-center justify-center gap-2">
          <p className="font-mono text-xs text-fg-soft">
            Afișate {visiblePhotos.length} din {photos.length} poze
          </p>
          <button
            type="button"
            onClick={loadMore}
            className="rounded-full border border-border bg-bg-alt px-5 py-2 font-mono text-xs text-fg hover:border-accent hover:text-accent transition-colors shadow-sm cursor-pointer"
          >
            Încarcă următoarele 50 de poze ↓
          </button>
        </div>
      )}

      {!hasMore && photos.length > PAGE_SIZE && (
        <div className="py-xl text-center">
          <p className="font-mono text-xs text-fg-soft">
            Ai ajuns la final · toate cele {photos.length} de poze sunt afișate ✨
          </p>
        </div>
      )}
    </div>
  );
}